import { Icons, SpinnerLoading } from '@cockpitai/components/atoms'
import { ChatInputIconAnimated } from '@cockpitai/components/atoms/ChatInputIconAnimated'
import { routesPath } from '@cockpitai/constants/routesPath'
import useInterval from '@cockpitai/hooks/useInterval'
import { useChatStore } from '@cockpitai/store/chat'
import { useSideBarStore } from '@cockpitai/store/navigation/useSideBarStore'
import { sendAnalyticsEvent } from '@cockpitai/utils/analytics'
import { isMobile } from '@cockpitai/utils/device'
import { MicIcon, SquareIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Controller } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'
import { twMerge } from 'tailwind-merge'
import { useSideBar } from '../../Sidebar/SideBarContext'
import { useChat } from '../useChat'

export function ChatInput() {
  // @ts-ignore
  const recognitionRef = useRef<SpeechRecognition | null>(null)

  const { setIsStartedChat } = useChatStore()

  const { pathname } = useLocation()

  const isActive = pathname?.includes('chat')

  const navigate = useNavigate()

  const propsChat = useChat()

  const [stop, setStop] = useState(true)

  const [stop2, setStop2] = useState(true)

  const [transcript, setTranscript] = useState('')
  const [recording, setRecording] = useState<boolean>(false)
  const [audioStream, setAudioStream] = useState<MediaStream | null>(null)

  const inputRef = useRef<string>('')

  const startRealTimeRecognition = async () => {
    if (
      !('webkitSpeechRecognition' in window) &&
      !('SpeechRecognition' in window)
    ) {
      alert('Seu navegador não suporta reconhecimento de voz.')
      return
    }

    setRecording(true)

    sendAnalyticsEvent('chat_voice')

    const SpeechRecognition =
      // @ts-ignore
      window.webkitSpeechRecognition || window.SpeechRecognition
    const recognition = new SpeechRecognition()

    recognition.lang = 'pt-BR'
    recognition.continuous = true
    recognition.interimResults = false

    recognition.onresult = (event: any) => {
      let resultadoFinal = ''
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          resultadoFinal += event.results[i][0].transcript
        }
      }
      setTranscript(resultadoFinal)
    }

    recognition.onerror = (event: any) => {
      console.error('Erro no reconhecimento:', event.error)
    }

    recognition.onend = () => {
      setRecording(false)
      console.log('Reconhecimento finalizado')
    }

    recognition.start()
    recognitionRef.current = recognition
  }

  const stopRealTimeRecognition = () => {
    setRecording(false)
    if (audioStream) {
      audioStream.getTracks().forEach((track) => track.stop())
      setAudioStream(null)
    }

    if (recognitionRef.current) {
      recognitionRef.current.stop()
      recognitionRef.current = null
    }
  }

  useInterval(() => {
    if (!isActive) {
      setStop(!stop)

      setStop2((state) => !!state)
    } else {
      setStop(true)
    }
  }, 5000)

  useEffect(() => {
    setTimeout(() => {
      if (!isActive) {
        setStop2(!stop)
      } else {
        setStop2(true)
      }
    }, 2500)
  }, [isActive, stop])

  useEffect(() => {
    if (transcript) {
      propsChat.setValue('question', transcript, {
        shouldValidate: true,
        shouldDirty: true,
      })
    }
  }, [transcript, propsChat.setValue])

  const { sidebarIsOpen } = useSideBar()

  const { isLocked: lockSideBarOpened } = useSideBarStore()

  return (
    <div
      className="input-chat-wrapper"
      data-is-sidebar-open={
        !isMobile() ? sidebarIsOpen || lockSideBarOpened : sidebarIsOpen
      }>
      <div className="max-intermediary relative bottom-3 left-auto flex lg:bottom-6 lg:w-input-chat lg:max-w-[60rem]">
        <form
          noValidate
          onSubmit={(e) => {
            e.preventDefault()

            const questionValue = inputRef.current?.trim()
            if (!questionValue) return

            setIsStartedChat(true)

            navigate(routesPath.dashboard.chat, { replace: true })

            propsChat.onSubmit(e)

            propsChat.setValue('question', '')
            inputRef.current = ''
          }}
          className={twMerge(
            'form-chat-input',
            !stop2 && 'animated-chat-input',
          )}>
          <button
            type="button"
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gray-700">
            <ChatInputIconAnimated stop={stop} />
          </button>
          <Controller
            control={propsChat.control}
            name="question"
            render={({ field }) => (
              <input
                required
                {...field}
                onChange={(e) => {
                  field.onChange(e)
                  inputRef.current = e.target.value
                }}
                disabled={propsChat.isLoading}
                autoComplete="off"
                id="chat_input"
                className="h-full w-full bg-transparent px-2 text-white text-opacity-75 outline-none placeholder:font-outfit placeholder:text-white placeholder:text-opacity-50 disabled:opacity-85"
                placeholder="Faça sua pergunta"
              />
            )}
          />
          <div className="flex gap-2">
            {import.meta.env.VITE_ENABLE_VOICE === 'true' && (
              <button
                type="button"
                disabled={propsChat.isLoading}
                onClick={
                  recording ? stopRealTimeRecognition : startRealTimeRecognition
                }
                className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gray-700">
                {recording ? (
                  <SquareIcon size={17} strokeWidth={2} />
                ) : (
                  <MicIcon size={17} strokeWidth={2} />
                )}
              </button>
            )}

            <button
              type="submit"
              disabled={propsChat.isLoading || !!audioStream}
              className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full">
              {propsChat.isLoading ? (
                <SpinnerLoading color="white" opacity={0.6} />
              ) : (
                <Icons.ArrowChat />
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
